<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, RouterLink, useRoute, useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlTabs from '../xl/XlTabs.vue';
import SessionCalendar from '../agenda/SessionCalendar.vue';
import PortalCalendarPopover from './PortalCalendarPopover.vue';
import PortalOfficeHours from './PortalOfficeHours.vue';
import PortalSessionRowSkeleton from './PortalSessionRowSkeleton.vue';
import { useAgendaLive } from '../../composables/useAgendaLive';
import PortalRecordingsList from './PortalRecordingsList.vue';
import PortalSessionRow from './PortalSessionRow.vue';
import { myRecordings, sessionsList, sessionsWeek } from '../../api/portal';
import { plural } from '../../helpers/plural';
import { shiftWeeks } from '../../helpers/calendarWeek';
import { memberWeekWindow, toGridSession } from '../../helpers/memberCalendar';
import { viewerTimeZone } from '../../helpers/greeting';
import { monthHeading, monthKey, timeState, zoneName } from '../../helpers/sessionTimes';
import { reportError } from '../../faro';
import { useEventStore } from '../../stores/event';

/**
 * Sessions & Office Hours (XN-4558): the prototype's events() page (build 30
 * Sep 12:40) on the members' API. Office Hours on top, then Upcoming — one
 * card per session or series, under month headings in the viewer's own zone —
 * and Recordings. The room is hosted around this page by PortalSessionsRoom.
 */
const route = useRoute();
const router = useRouter();
const eventStore = useEventStore();

const agency = computed(() => eventStore.event?.value?.login_event_name || 'your agency');
const timeZone = viewerTimeZone();
const zone = zoneName(timeZone);

// A minute tick: Join appears 15 minutes before, and an ended session leaves.
const now = ref(new Date());
let tick = null;

const tabFrom = (query) => (query.tab === 'recordings' ? 'recordings' : 'upcoming');
const tab = ref(tabFrom(route.query));
watch(tab, (value) => {
  if (tabFrom(route.query) === value) return;
  const { tab: _drop, ...rest } = route.query;
  router.replace({ query: value === 'recordings' ? { ...rest, tab: 'recordings' } : rest });
});
// The shell's Sessions link, or Back, changes the URL under the page.
watch(() => route.query.tab, () => { tab.value = tabFrom(route.query); });

// Below 600px the week has no room; the ticket asks for one column there.
const phoneQuery = window.matchMedia?.('(max-width: 600px)');
const isPhone = ref(Boolean(phoneQuery?.matches));
const onPhoneChange = (event) => { isPhone.value = event.matches; };

const viewFrom = (query) => (query.view === 'calendar' ? 'calendar' : 'list');
const view = ref(viewFrom(route.query));
watch(view, (value) => {
  if (viewFrom(route.query) === value) return;
  const { view: _drop, ...rest } = route.query;
  router.replace({ query: value === 'calendar' ? { ...rest, view: 'calendar' } : rest });
});
watch(() => route.query.view, () => { view.value = viewFrom(route.query); });

const showCalendar = computed(() => tab.value === 'upcoming' && view.value === 'calendar' && !isPhone.value);
const views = [{ id: 'list', label: 'List' }, { id: 'calendar', label: 'Calendar' }];

// Back from a session lands on the list as it was left: tab and view live in
// the URL, the scroll and the calendar's week in this history entry (the
// router keeps none without a scrollBehavior). Read once and removed, so a
// refresh starts at the top, this week.
const { sessionsScroll, sessionsAnchor, ...kept } = window.history.state || {};
if (sessionsScroll != null || sessionsAnchor != null) {
  window.history.replaceState(kept, '');
  // The router keeps its own copy of the state and merges it back on the next
  // push, so the stripped key would return: one replace refreshes it.
  router.replace({ query: route.query, force: true });
}
let restoreTo = sessionsScroll;
const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
onBeforeRouteLeave(() => {
  window.history.replaceState({ ...window.history.state, sessionsScroll: window.scrollY, sessionsAnchor: isoDay(anchor.value) }, '');
});

const state = ref('loading');
const recordingsState = ref('loading');
// What the active tab shows is what the scroll position belongs to.
const tabReady = computed(() => (tab.value === 'recordings' ? recordingsState.value : state.value) === 'ready');
watch(tabReady, async (ready) => {
  if (!ready || !restoreTo) return;
  const top = restoreTo;
  restoreTo = null;
  await nextTick();
  window.scrollTo(0, top);
});
const sessions = ref([]);
const officeHours = ref(null);

/*
 * Whether this hub has Office Hours, as the last visit found — so the first
 * paint can hold the card's place, or not, before the answer is here. A hub
 * with a desk keeps its tabs where they will be (QA: they moved 167px when
 * the card landed); one without does not reserve a card that never comes.
 * Unknown (a first visit) expects one: most hubs keep a desk.
 */
const OH_KEY = `xl.portal.officeHours.${window.store?.subdomain ?? ''}`;
const rememberedDesk = () => {
  try {
    return localStorage.getItem(OH_KEY) !== '0';
  } catch {
    return true;
  }
};
const expectsDesk = ref(rememberedDesk());
// The server cut the list at its cap (XN-4688); only an explicit false says so.
const more = ref(0); // how many the server cut the list to, 0 when whole
const recordings = ref([]);

// Quiet is a refresh behind what's shown: no skeleton, and a failure keeps the list.
async function load({ quiet = false } = {}) {
  if (!quiet) state.value = 'loading';
  try {
    const data = await sessionsList();
    sessions.value = data?.sessions ?? [];
    officeHours.value = data?.office_hours ?? null;
    expectsDesk.value = Boolean(officeHours.value);
    try {
      localStorage.setItem(OH_KEY, officeHours.value ? '1' : '0');
    } catch {
      // Not remembered: the next first paint expects a desk again.
    }
    more.value = data?.complete === false ? (data.sessions?.length ?? 0) : 0;
    state.value = 'ready';
  } catch (error) {
    reportError(error);
    if (!quiet) state.value = 'error';
  }
}

async function loadRecordings() {
  recordingsState.value = 'loading';
  try {
    recordings.value = await myRecordings();
    recordingsState.value = 'ready';
  } catch (error) {
    reportError(error);
    recordingsState.value = 'error';
  }
}

const upcoming = computed(() => sessions.value.filter((session) => !timeState(session, now.value).ended));

// Month headings where the viewer is, soonest first. A session on now began
// before any that hasn't, so start order already puts it first.
const groups = computed(() => {
  const months = new Map();
  const byStart = [...upcoming.value].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  for (const session of byStart) {
    const key = monthKey(session.starts_at, timeZone);
    if (!months.has(key)) months.set(key, { key, heading: monthHeading(session.starts_at, timeZone), sessions: [] });
    months.get(key).sessions.push(session);
  }
  return [...months.values()];
});

const tabs = computed(() => [
  { id: 'upcoming', label: state.value === 'ready' ? `Upcoming (${upcoming.value.length}${more.value ? '+' : ''})` : 'Upcoming' },
  { id: 'recordings', label: recordingsState.value === 'ready' ? `Recordings (${recordings.value.length})` : 'Recordings' },
]);

// When a card leaves, the next is on the server, not here: a series card stands
// for every date, and a capped list (XN-4688) has more behind its last row.
const leaves = (session) => !!session && (more.value > 0 || Number(session.series?.dates_left) > 1);

// One session's details open at a time, as on the staff list (XN-4745).
const openId = ref(null);
const toggleRow = (id) => { openId.value = openId.value === id ? null : id; };

const drop = (id) => {
  if (openId.value === id) openId.value = null;
  const gone = sessions.value.find((session) => session.id === id);
  sessions.value = sessions.value.filter((session) => session.id !== id);
  if (leaves(gone)) load({ quiet: true });
};

const advance = () => {
  const before = now.value;
  now.value = new Date();
  const ends = (entry) => entry && !timeState(entry, before).ended && timeState(entry, now.value).ended;
  // Asked until a slot still to come arrives, as on Home: a server clock a few
  // seconds behind answers with the slot that has just closed.
  const deskOver = Boolean(officeHours.value) && timeState(officeHours.value, now.value).ended;
  // A card that has more behind it, or the desk's slot, ending: the next one is
  // on the server. The desk card shows closed meanwhile, rather than disappearing.
  if (sessions.value.some((session) => leaves(session) && ends(session)) || deskOver) {
    load({ quiet: true });
  }
};


// Noon, so no zone shifts the saved day.
const anchor = ref(sessionsAnchor ? new Date(`${sessionsAnchor}T12:00:00`) : new Date());
const weekState = ref('idle');
const weekEntries = ref([]);
const gridSessions = computed(() => weekEntries.value.map((entry) => toGridSession(entry, timeZone)));
let weekRequest = 0;

async function loadWeek() {
  const ticket = ++weekRequest;
  weekState.value = 'loading';
  try {
    const { from, to } = memberWeekWindow(anchor.value);
    const entries = await sessionsWeek(from, to);
    if (ticket !== weekRequest) return;
    weekEntries.value = entries;
    weekState.value = 'ready';
  } catch (error) {
    if (ticket !== weekRequest) return;
    reportError(error);
    weekState.value = 'error';
  }
}

const moveWeek = (weeks) => { anchor.value = shiftWeeks(anchor.value, weeks); };
const thisWeek = () => { anchor.value = new Date(); };
watch(anchor, () => { if (showCalendar.value) loadWeek(); });
// Every showing refetches (a seat saved on the list must show); chips stay up meanwhile.
watch(showCalendar, (shown) => { if (shown) loadWeek(); }, { immediate: true });

const popover = reactive({ open: false, anchor: null, id: null });
const popoverSession = computed(() => weekEntries.value.find((entry) => entry.id === popover.id) ?? null);
const openChip = ({ session, event }) => {
  popover.id = session.id;
  popover.anchor = event?.currentTarget ?? null;
  popover.open = true;
};
// One seat, two copies (list and week): a write in either view lands in both.
// A list card's seat covers its whole series, so every date of it in the week follows.
const setSeat = ({ id, seat, seriesId = null }) => {
  const hit = (entry) => entry.id === id || (seriesId !== null && entry.series?.id === seriesId);
  sessions.value = sessions.value.map((entry) => (entry.id === id ? { ...entry, seat } : entry));
  weekEntries.value = weekEntries.value.map((entry) => (hit(entry) ? { ...entry, seat } : entry));
};
const onPopoverGone = (id) => {
  weekEntries.value = weekEntries.value.filter((entry) => entry.id !== id);
  drop(id);
};
const calendarBox = ref(null);
// Anything that changes what the grid shows leaves the popover's chip behind.
watch([showCalendar, anchor, () => weekState.value === 'error'], () => { popover.open = false; });

// A covered Office Hours day or a moved session shows up without a reload.
useAgendaLive(() => {
  load({ quiet: true });
  if (showCalendar.value) loadWeek();
});

onMounted(() => {
  phoneQuery?.addEventListener?.('change', onPhoneChange);
  load();
  loadRecordings();
  tick = setInterval(advance, 60000);
});
onBeforeUnmount(() => {
  clearInterval(tick);
  phoneQuery?.removeEventListener?.('change', onPhoneChange);
});
</script>

<template>
  <section class="sessions">
    <h1 class="sessions__title">Sessions &amp; Office Hours</h1>
    <p class="sessions__sub">All online. Free, short, run by real people at {{ agency }}.</p>
    <p class="sessions__zone">Times are in {{ zone }}.</p>

    <PortalOfficeHours
      :office-hours="officeHours"
      :loading="state === 'loading' && expectsDesk"
      :agency="agency"
      :time-zone="timeZone"
      :now="now"
    />

    <div class="sessions__controls">
      <XlTabs v-model="tab" :tabs="tabs" aria-label="Sessions" />
      <span class="sessions__spacer" />
      <XlTabs v-if="tab === 'upcoming' && !isPhone" v-model="view" :tabs="views" aria-label="View" />
    </div>

    <template v-if="tab === 'upcoming'">
      <div v-if="showCalendar" ref="calendarBox" class="sessions__week">
        <div v-if="weekState === 'error'" class="sessions__error" role="alert">
          <p>This week couldn't be loaded.</p>
          <XlButton variant="ghost" size="sm" data-testid="week-retry" @click="loadWeek">Try again</XlButton>
        </div>
        <SessionCalendar
          readonly
          :sessions="gridSessions"
          :anchor="anchor"
          :loading="weekState === 'loading'"
          :error="weekState === 'error'"
          :open-id="popover.open ? popover.id : null"
          @move="moveWeek"
          @today="thisWeek"
          @open="openChip"
        />
        <PortalCalendarPopover
          v-model:open="popover.open"
          :anchor="popover.anchor"
          :boundary="calendarBox"
          :session="popoverSession"
          :now="now"
          :time-zone="timeZone"
          @seat="setSeat"
          @gone="onPopoverGone"
        />
      </div>

      <!-- The list's own shape while it loads — a month heading and rows like
           PortalSessionRow — so nothing moves when it lands. -->
      <div v-else-if="state === 'loading'" class="sessions__loading" data-testid="sessions-loading">
        <section class="sessions__month">
          <div class="sessions__sh">
            <h2><XlSkeleton width="120px" height="0.9em" radius="6px" class="sessions__sk" /></h2>
            <span class="sessions__ln" />
          </div>
          <PortalSessionRowSkeleton title-width="58%" meta-width="40%" />
          <PortalSessionRowSkeleton title-width="46%" meta-width="34%" />
          <PortalSessionRowSkeleton title-width="64%" meta-width="38%" />
        </section>
      </div>

      <div v-else-if="state === 'error'" class="sessions__error" role="alert">
        <p>Your sessions couldn't be loaded.</p>
        <XlButton variant="ghost" size="sm" data-testid="sessions-retry" @click="load()">Try again</XlButton>
      </div>

      <!-- XN-4688 (Greg's frame 1928:9110). The frame's inbox icon is still to come from design. -->
      <XlEmptyState
        v-else-if="!upcoming.length"
        class="sessions__empty"
        headline="Nothing's scheduled right now"
        :body="`When ${agency} plans a session, it'll show up here.`"
      >
        <RouterLink to="/hub/messages" class="sessions__broker">Message your broker</RouterLink>
      </XlEmptyState>

      <template v-else>
        <section v-for="month in groups" :key="month.key" class="sessions__month">
          <div class="sessions__sh">
            <h2 data-testid="month-heading">{{ month.heading }}</h2>
            <span class="sessions__ln" />
          </div>
          <PortalSessionRow
            v-for="session in month.sessions"
            :key="session.id"
            :session="session"
            :now="now"
            :time-zone="timeZone"
            :open="openId === session.id"
            @toggle="toggleRow"
            @gone="drop"
            @seat="setSeat"
          />
        </section>
        <p v-if="more" class="sessions__more" data-testid="sessions-more">Showing the next {{ plural(more, 'session') }}. More are coming up.</p>
      </template>
    </template>

    <PortalRecordingsList
      v-else
      :recordings="recordings"
      :state="recordingsState"
      :agency="agency"
      @retry="loadRecordings"
    />
  </section>
</template>

<style scoped>
/* Prototype events() (build 30 Sep 12:40): h1 32px .fr, sub 16.5px, `.sh` month headings. */
.sessions__title { font-size: 32px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.sessions__sub { font-size: 16.5px; color: var(--xl-color-on-surface-variant); margin: 0 0 4px; }
.sessions__zone { font-size: 13px; color: var(--xl-color-secondary-text); margin: 0 0 22px; }
.sessions__controls { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
.sessions__controls :deep(.xl-tabs) { margin: 0; }
.sessions__spacer { flex: 1; }
/* Prototype calView's `card p`: the week sits on a white card. */
.sessions__week {
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg);
  padding: 16px 18px; box-shadow: var(--xl-shadow-1);
}
.sessions__loading { display: block; }
.sessions__sk { vertical-align: middle; width: 120px; }
.sessions__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  padding: 18px 20px; border-radius: 16px; font-size: 14.5px;
  background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.sessions__error p { margin: 0; }
.sessions__empty {
  border-radius: 24px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  box-shadow: 0 1px 2px rgba(28, 23, 32, .04);
}
.sessions__broker {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 15px;
  border: 1px solid var(--xl-color-outline);
  border-radius: 999px;
  background: var(--xl-color-surface);
  color: var(--xl-color-primary);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.sessions__broker:hover { background: var(--xl-color-primary-container); }
.sessions__more { margin: 18px 0 0; font-size: 12.5px; color: var(--xl-color-secondary-text); text-align: center; }
.sessions__sh { display: flex; align-items: baseline; gap: 12px; margin: 26px 0 14px; }
.sessions__month:first-child .sessions__sh { margin-top: 4px; }
.sessions__sh h2 {
  margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--xl-color-secondary-text);
}
.sessions__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
</style>
